/**
 * Theme state. Writes data-theme on <html>, persists, and re-renders
 * subscribers. Charts that need to recolour can just read `theme` instead of
 * each mounting its own MutationObserver (the old app had three).
 */
import { useCallback, useEffect, useState } from "react";

export const THEMES = ["light", "dark", "soft", "contrast"] as const;
export type Theme = (typeof THEMES)[number];

const KEY = "specta.theme";

function read(): Theme {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved && (THEMES as readonly string[]).includes(saved)) return saved as Theme;
  } catch {
    /* ignore */
  }
  return "light";
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(read);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  return { theme, setTheme, themes: THEMES };
}
